import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import { useAuth } from '../../context/AuthContext.jsx'
import api from '../../lib/api.js'
import { AdminTable, AdminToolbar, Td } from './AdminTable.jsx'
import AdminUserFormModal from './AdminUserFormModal.jsx'

/* Admin — users CRUD (super-admin). Create/edit (multi-company assignment),
 * delete. GET /admin/users/ returns the full dataset. Roles are managed
 * per-company in the company details modal. */
export default function AdminUsersTab() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { confirm, toast } = useFeedback()
  const [all, setAll] = useState([])
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [form, setForm] = useState(null) // {} new | {user}

  const load = useCallback(() => {
    setLoading(true)
    api.get('/admin/users/').then(({ data }) => setAll(data?.users || [])).finally(() => setLoading(false))
  }, [])
  useEffect(() => {
    load()
    api.get('/admin/companies/').then(({ data }) => setCompanies(data?.companies || [])).catch(() => {})
  }, [load])

  const remove = async (u) => {
    const ok = await confirm({ tone: 'danger', destructive: true, title: t('admin.users.confirmDelete'), message: u.email, confirmLabel: t('common.delete'), cancelLabel: t('common.cancel') })
    if (!ok) return
    try { await api.delete(`/admin/users/${u.id}`); toast(t('admin.users.deleted'), 'success'); load() }
    catch (e) { toast(e?.response?.data?.error || t('admin.errorGeneric'), 'error') }
  }

  const rows = all.filter((u) => {
    const s = q.trim().toLowerCase()
    return !s || `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(s)
  })

  const columns = [
    { key: 'name', label: t('admin.users.name') }, { key: 'email', label: t('admin.users.email') },
    { key: 'companies', label: t('admin.users.companies'), align: 'right' }, { key: 'roles', label: t('admin.users.roles') },
    { key: 'status', label: t('admin.companies.status') }, { key: 'actions', label: t('common.actions'), align: 'right' },
  ]

  return (
    <>
      <AdminToolbar search={q} onSearch={setQ} placeholder={t('admin.users.search')}
        action={<Button variant="primary" icon="plus" onClick={() => setForm({})}>{t('admin.users.new')}</Button>} />
      <AdminTable columns={columns} rows={rows} loading={loading} empty={t('admin.users.empty')}
        renderRow={(u) => (
          <>
            <Td strong>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}</Td>
            <Td>{u.email}</Td>
            <Td align="right">{(u.company_ids || []).length}</Td>
            <Td><div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{(u.roles || []).map((r) => <Badge key={r} tone={r === 'super-administrateur' ? 'info' : 'neutral'}>{r}</Badge>)}</div></Td>
            <Td><Badge tone={u.is_active ? 'success' : 'neutral'}>{u.is_active ? t('admin.active') : t('admin.inactive')}</Badge></Td>
            <Td align="right"><div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
              <IconButton icon="pencil" variant="primary" title={t('common.edit')} onClick={() => setForm({ user: u })} />
              {u.id !== user?.id && <IconButton icon="trash" variant="danger" title={t('common.delete')} onClick={() => remove(u)} />}
            </div></Td>
          </>
        )} />
      {form && <AdminUserFormModal user={form.user} companies={companies} onClose={() => setForm(null)} onSaved={() => { setForm(null); toast(t('admin.users.saved'), 'success'); load() }} />}
    </>
  )
}
